// Export Files Tool - Provides selective file export for workspace
// Extends WorkspaceTool - has access to appType, toolSystem, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class ExportFilesTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "exportFilesTool";
  }

  /**
   * Export selected files as a zip archive
   * @param {string[]} paths - File or directory paths to export (relative to workspace root)
   * @param {string} [zipName="app-export"] - Base name for the zip file (without .zip)
   * @returns {Promise<Object>} Result with download URL and metadata
   */
  async exportFiles(paths = [], zipName = "app-export") {
    try {
      // Validate paths
      if (!Array.isArray(paths) || paths.length === 0) {
        return this.returnError({}, "Paths array is required to export files");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.exportFilesForBrowser(paths, zipName);
      }
      if (this.appType === "local") {
        return await this.exportFilesForLocal(paths, zipName);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for exportFiles`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|exportfiles|error",
          color1: "red",
        },
        "Failed to export files",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Export files for browser apps using Lightning FS
   */
  async exportFilesForBrowser(paths, zipName) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    const normalizedPaths = paths.map((path) => this.normalizeAndValidatePath(path));
    if (normalizedPaths.some((path) => !path)) {
      return this.returnError({}, "Invalid file path detected in export list");
    }

    const expandedFiles = await this.expandPathsForBrowser(normalizedPaths, fs);
    if (expandedFiles.length === 0) {
      return this.returnError({}, "No files found to export");
    }

    const { blob, fileName } = await this.createZipForBrowser(
      expandedFiles,
      zipName,
      fs
    );

    const downloadUrl = URL.createObjectURL(blob);

    logger.log(
      {
        tags: "tools|exportfiles|info",
        color1: "lime",
      },
      "Exported files as zip",
      {
        fileName,
        fileCount: expandedFiles.length,
        size: blob.size,
      }
    );

    return this.returnSuccess(
      {
        downloadUrl,
        fileName,
        fileCount: expandedFiles.length,
        size: blob.size,
        files: expandedFiles,
      },
      `Exported ${expandedFiles.length} file(s) successfully`
    );
  }

  /**
   * Expand selected paths to a full list of files
   */
  async expandPathsForBrowser(paths, fs) {
    const fileSet = new Set();

    for (const path of paths) {
      const normalizedPath = path === "/" ? "/" : path;
      const filesystemPath = `/files${normalizedPath}`;

      try {
        const stats = await fs.stat(filesystemPath);
        const isDir =
          typeof stats.isDirectory === "function"
            ? stats.isDirectory()
            : stats.type === "dir";

        if (isDir) {
          const directoryFiles = await this.collectDirectoryFiles(
            filesystemPath,
            normalizedPath,
            fs
          );
          directoryFiles.forEach((filePath) => fileSet.add(filePath));
        } else {
          fileSet.add(normalizedPath);
        }
      } catch (error) {
        if (error.code === "ENOENT") {
          logger.warn(
            {
              tags: "tools|exportfiles|missing",
              color1: "yellow",
            },
            `Export path not found: ${path}`
          );
          continue;
        }
        throw error;
      }
    }

    return Array.from(fileSet);
  }

  /**
   * Recursively collect files under a directory
   */
  async collectDirectoryFiles(filesystemPath, relativePath, fs) {
    const files = [];
    const entries = await fs.readdir(filesystemPath);

    for (const entry of entries) {
      const entryFilesystemPath =
        filesystemPath === "/files"
          ? `/files/${entry}`
          : `${filesystemPath}/${entry}`;
      const entryRelativePath =
        relativePath === "/" ? `/${entry}` : `${relativePath}/${entry}`;

      const stats = await fs.stat(entryFilesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (isDir) {
        const nestedFiles = await this.collectDirectoryFiles(
          entryFilesystemPath,
          entryRelativePath,
          fs
        );
        files.push(...nestedFiles);
      } else {
        files.push(entryRelativePath);
      }
    }

    return files;
  }

  /**
   * Create a zip archive from file paths
   */
  async createZipForBrowser(filePaths, zipName, fs) {
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();

    for (const filePath of filePaths) {
      const filesystemPath = `/files${filePath}`;
      const zipPath = filePath.replace(/^\/+/, "");
      const content = await fs.readFile(filesystemPath);
      zip.file(zipPath, content);
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const safeName = this.normalizeZipName(zipName);

    return {
      blob,
      fileName: `${safeName}.zip`,
    };
  }

  /**
   * Normalize and validate path - ensures it's relative to workspace root
   * @param {string} path - Path to normalize
   * @returns {string|null} Normalized path or null if invalid
   */
  normalizeAndValidatePath(path) {
    if (typeof path !== "string") {
      return null;
    }

    if (path.trim() === "") {
      return null;
    }

    // Remove leading slashes to ensure relative path
    let normalized = path.replace(/^\/+/, "");

    // Prevent directory traversal
    if (normalized.includes("..")) {
      return null;
    }

    // Normalize path separators
    normalized = normalized.replace(/\\/g, "/");

    return normalized === "" ? "/" : `/${normalized}`;
  }

  /**
   * Export files for local apps (Revolt Desktop + workspaceLocalAppAPI).
   *
   * Even though local app files already live on the user's disk, we still
   * produce a downloadable .zip so the "export" surface matches browser apps
   * and so the user can hand off the bundle (email, upload, import into a
   * browser app, etc.). Reads go through ReadFileTool (IPC), directory
   * expansion through ListDirectoryTool (IPC).
   */
  async exportFilesForLocal(paths, zipName) {
    const listTool = this.toolSystem?.listDirectoryTool;
    const readTool = this.toolSystem?.readFileTool;
    if (!listTool || !readTool) {
      return this.returnError(
        {},
        "ListDirectoryTool or ReadFileTool is not available"
      );
    }

    const normalizedPaths = paths.map((path) =>
      this.normalizeAndValidatePath(path)
    );
    if (normalizedPaths.some((path) => !path)) {
      return this.returnError({}, "Invalid file path detected in export list");
    }

    const expandedFiles = await this.expandPathsForLocal(
      normalizedPaths,
      listTool,
      readTool
    );
    if (expandedFiles.length === 0) {
      return this.returnError({}, "No files found to export");
    }

    const { blob, fileName } = await this.createZipForLocal(
      expandedFiles,
      zipName,
      readTool
    );

    const downloadUrl = URL.createObjectURL(blob);

    logger.log(
      { tags: "tools|exportfiles|info|local", color1: "lime" },
      "Exported files as zip (local)",
      { fileName, fileCount: expandedFiles.length, size: blob.size }
    );

    return this.returnSuccess(
      {
        downloadUrl,
        fileName,
        fileCount: expandedFiles.length,
        size: blob.size,
        files: expandedFiles,
      },
      `Exported ${expandedFiles.length} file(s) successfully`
    );
  }

  /**
   * Expand selected paths into a flat file list using ListDirectoryTool (IPC).
   */
  async expandPathsForLocal(paths, listTool, readTool) {
    const fileSet = new Set();

    for (const path of paths) {
      const normalizedPath = path === "/" ? "/" : path;

      // Root export → list everything recursively.
      if (normalizedPath === "/") {
        const listResult = await listTool.listDirectory("/", true, false, true);
        if (!listResult?.success) continue;
        for (const entry of listResult.data?.entries || []) {
          if (entry.type === "file" && entry.path) {
            fileSet.add(entry.path);
          }
        }
        continue;
      }

      const exists = await readTool.checkExists(normalizedPath);
      if (!exists) {
        logger.warn(
          { tags: "tools|exportfiles|missing|local", color1: "yellow" },
          `Export path not found (local): ${path}`
        );
        continue;
      }

      // Try as a directory first; fall back to single-file.
      const listResult = await listTool.listDirectory(
        normalizedPath,
        true,
        false,
        true
      );
      if (listResult?.success) {
        const entries = listResult.data?.entries || [];
        if (entries.length > 0) {
          for (const entry of entries) {
            if (entry.type === "file" && entry.path) {
              fileSet.add(entry.path);
            }
          }
          continue;
        }
      }

      fileSet.add(normalizedPath);
    }

    return Array.from(fileSet);
  }

  /**
   * Build the zip by reading each file over IPC as base64 (binary-safe).
   */
  async createZipForLocal(filePaths, zipName, readTool) {
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();

    for (const filePath of filePaths) {
      const zipPath = filePath.replace(/^\/+/, "");
      const readResult = await readTool.readFile(filePath, "base64");
      if (!readResult?.success) {
        logger.warn(
          { tags: "tools|exportfiles|read|local", color1: "yellow" },
          `Failed to read file for export (local): ${filePath}`,
          readResult?.message
        );
        continue;
      }
      const content = readResult.data?.content;
      if (typeof content !== "string") continue;
      zip.file(zipPath, content, { base64: true });
    }

    const blob = await zip.generateAsync({ type: "blob" });
    const safeName = this.normalizeZipName(zipName);

    return {
      blob,
      fileName: `${safeName}.zip`,
    };
  }

  /**
   * Normalize zip file name
   */
  normalizeZipName(name) {
    if (!name || typeof name !== "string") {
      return "app-export";
    }

    const trimmed = name.trim().replace(/\.zip$/i, "");
    if (!trimmed) {
      return "app-export";
    }

    return trimmed.replace(/[^\w-]+/g, "-");
  }
}
